export type ChimeTile = {
  tileId: number;
  boundAttendeeId?: string;
  localTile: boolean;
  active: boolean;
  /** Chime removed the video tile while the attendee is still present (camera off). */
  removed?: boolean;
  /** A screen share; its `boundAttendeeId` is the sharer's `<attendeeId>#content`. */
  isContent?: boolean;
};

/**
 * Tiles stay in the list until Chime reports `videoTileWasRemoved`. They must not be
 * dropped while inactive: the SDK only sets `active` once a video element is bound, so
 * removing them would prevent the element from ever rendering.
 */
export function upsertChimeTile(tiles: ChimeTile[], next: ChimeTile): ChimeTile[] {
  // Starting video again may allocate a new tile id for the same attendee. Replace the
  // camera-off placeholder instead of rendering a duplicate participant.
  const without = tiles.filter(
    (t) =>
      t.tileId !== next.tileId &&
      (!next.boundAttendeeId || t.boundAttendeeId !== next.boundAttendeeId),
  );
  return [...without, next].sort((a, b) => a.tileId - b.tileId);
}

export function removeChimeTile(tiles: ChimeTile[], tileId: number): ChimeTile[] {
  return tiles.filter((t) => t.tileId !== tileId);
}

/** Keep the participant block when Chime removes their video because their camera is off. */
export function deactivateChimeTile(tiles: ChimeTile[], tileId: number): ChimeTile[] {
  return tiles.map((tile) =>
    tile.tileId === tileId ? { ...tile, active: false, removed: true } : tile,
  );
}

/** Presence, unlike video state, tells us that the participant actually left the meeting. */
export function removeChimeTilesForAttendee(
  tiles: ChimeTile[],
  attendeeId: string,
): ChimeTile[] {
  return tiles.filter((tile) => tile.boundAttendeeId !== attendeeId);
}

/**
 * Chime can emit a trailing `videoTileDidUpdate` after an attendee's presence goes false,
 * re-adding the tile that presence just removed and leaving a nameless ghost participant on
 * screen. Presence is the authority on who is in the call, so filter on it at render time
 * rather than trying to win the event race.
 */
export function visibleChimeTiles(
  tiles: ChimeTile[],
  absentAttendeeIds: ReadonlySet<string>,
): ChimeTile[] {
  if (absentAttendeeIds.size === 0) return tiles;
  return tiles.filter(
    (tile) => !tile.boundAttendeeId || !absentAttendeeIds.has(tile.boundAttendeeId),
  );
}

/**
 * Chime only creates a tile once someone's camera starts, so a person who joins with the
 * camera off would never be drawn. Add a camera-off tile (negative id, never bound to video)
 * for each present attendee without one. Screen-share attendees (`#content`) are skipped.
 */
export function withCameraOffPlaceholders(
  tiles: ChimeTile[],
  presentAttendeeIds: readonly string[],
  localAttendeeId: string | undefined,
): ChimeTile[] {
  const tiled = new Set(tiles.map((tile) => tile.boundAttendeeId));
  const missing = presentAttendeeIds
    .filter((id) => !id.endsWith('#content') && !tiled.has(id))
    .sort();
  return [
    ...tiles,
    ...missing.map((id, index) => ({
      tileId: -(index + 1),
      boundAttendeeId: id,
      localTile: id === localAttendeeId,
      active: false,
      removed: true,
    })),
  ];
}

/** Gallery layout: the smallest square-ish grid that fits every tile without scrolling. */
export function chimeGridColumns(tileCount: number): number {
  return Math.ceil(Math.sqrt(Math.max(tileCount, 1)));
}

/** A shared screen is the main tile; otherwise Chime's active speaker, then a remote participant. */
export function selectSpeakerTile(
  tiles: ChimeTile[],
  activeSpeakerId: string | undefined,
): ChimeTile | undefined {
  return (
    tiles.find((tile) => tile.isContent) ??
    tiles.find((tile) => tile.boundAttendeeId === activeSpeakerId) ??
    tiles.find((tile) => !tile.localTile) ??
    tiles[0]
  );
}

/** Someone other than `localAttendeeId` is sharing their screen, so only one share runs at a time. */
export function otherScreenShareActive(tiles: ChimeTile[], localAttendeeId: string | undefined): boolean {
  return tiles.some((tile) => tile.isContent && tile.boundAttendeeId !== `${localAttendeeId}#content`);
}

/** Screen share needs `getDisplayMedia`, which iOS Safari and most mobile browsers lack. */
export function screenShareSupported(nav: { mediaDevices?: { getDisplayMedia?: unknown } } | undefined): boolean {
  return typeof nav?.mediaDevices?.getDisplayMedia === 'function';
}
